import {Inject, Injectable} from '@angular/core';
import {CareproviderUnitItem} from '../../../types/CareproviderUnitItem';
import {SchemaCodeSnippetIdEnum} from '../../../enums/SchemaEnums';
import {EnumsService} from '../../../services/enums.service';
import {WeekDayIndexesEnum, WeekDaysEnglishEnum} from '../../../enums/CalendarEnums';
import {DOCUMENT} from '@angular/common';
import {CodeSnippetsService} from '../../../services/code-snippets.service';
import {ToolsService} from '../../../services/tools.service';
import {UrlBuilderService} from '../../../services/url-builder.service';
import * as dayjs from 'dayjs';
import {AppConfigService} from '../../../services/app-config.service';

@Injectable()
export class CareproviderUnitSchemaService {
  constructor(
    private enumsService: EnumsService,
    private headerScriptService: CodeSnippetsService,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
    @Inject(DOCUMENT) private doc: any,
    private appConfigService: AppConfigService,
  ) {}

  public addSchema(clinic: CareproviderUnitItem): void {
    const schema = this.createMedicalClinicJsonSchema(clinic);
    this.headerScriptService.addScriptToHead(schema);

    const schema2 = this.createMedicalWebPageJsonSchema(clinic);
    this.headerScriptService.addScriptToHead(schema2);
  }

  public removeSchema(): void {
    this.headerScriptService.removeScriptsFromHead(`#${SchemaCodeSnippetIdEnum.MEDICAL_CLINIC}`);
    this.headerScriptService.removeScriptsFromHead(`#${SchemaCodeSnippetIdEnum.MEDICAL_WEBPAGE}`);
  }

  private createMedicalClinicJsonSchema(clinic: CareproviderUnitItem): any {
    const jsonSchema = {
      '@context': 'https://schema.org',
      '@type': 'MedicalClinic',
      name: clinic.name,
    };

    const address = {};
    if (clinic.address) {
      address['@type'] = 'PostalAddress';
      address['addressCountry'] = 'Sverige';
      if (clinic.address.street) {
        address['streetAddress'] = clinic.address.street;
      }
      if (clinic.address.city) {
        address['addressLocality'] = clinic.address.city;
      }
      if (clinic.address.zip) {
        address['postalCode'] = clinic.address.zip;
      }
      if (clinic.address.county) {
        address['addressRegion'] = clinic.address.county;
      }
      jsonSchema['address'] = address;
    }

    if (clinic.caregiverUnitImages) {
      const {images, thumbnailUrl, logoUrl} = clinic.caregiverUnitImages;
      if (images?.length) {
        jsonSchema['image'] = this.tools.getImageUrl(images[0].url);
      } else if (thumbnailUrl) {
        jsonSchema['image'] = this.tools.getImageUrl(thumbnailUrl);
      }
      if (logoUrl) {
        jsonSchema['logo'] = this.tools.getImageUrl(logoUrl);
      }
    }

    const openHours = [];
    if (clinic.serviceHours && clinic.serviceHours.surgeryHours) {
      clinic.serviceHours.surgeryHours.forEach((hours) => {
        for (let i = hours.fromDay; i <= hours.toDay; i++) {
          const dayKey = this.enumsService.getEnumKeyByEnumValue(WeekDayIndexesEnum, i);
          openHours.push({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: `https://schema.org/${WeekDaysEnglishEnum[dayKey]}`,
            opens: hours.fromTime,
            closes: hours.toTime,
          });
        }
      });
      jsonSchema['openingHoursSpecification'] = openHours;
    }

    const employee = clinic.staffMembers?.map((employee) => {
      return {
        '@type': 'Person',
        name: employee.name,
        jobTitle: this.tools.removeHtmlTags(employee.description),
      };
    });
    if (employee) {
      jsonSchema['employee'] = employee;
    }

    const aggregateRating = {};
    if (clinic.reviewSummary && clinic.reviewSummary.rating > 0 && clinic.reviewSummary.reviews > 0) {
      aggregateRating['@type'] = 'AggregateRating';
      aggregateRating['ratingValue'] = clinic.reviewSummary.rating;
      aggregateRating['reviewCount'] = clinic.reviewSummary.reviews;
      jsonSchema['aggregateRating'] = aggregateRating;
    }

    const review = [];
    const filteredReviews =
      clinic.reviews && clinic.reviews.length ? clinic.reviews.filter((item) => item.average > 0) : null;
    if (filteredReviews) {
      filteredReviews.forEach((item) => {
        review.push({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: item.author,
          },
          datePublished: item.metaDateCreatedFormatted,
          reviewBody: this.tools.removeHtmlTags(item.comment),
          reviewRating: {
            '@type': 'Rating',
            bestRating: 5,
            ratingValue: item.average,
            worstRating: 1,
          },
        });
      });
      jsonSchema['review'] = review;
    }

    const knowsAbout = [];
    if (clinic.careServices && clinic.careServices.length) {
      clinic.careServices.forEach((service) => {
        const offers = {
          '@type': 'Offer',
          availability: 'http://schema.org/InStock',
        };
        if (service.price && service.price) {
          offers['price'] = service.price;
          offers['priceCurrency'] = 'SEK';
        }

        const serviceSchema = {
          '@type': 'Service',
          name: service.name,
          description: this.tools.removeHtmlTags(service.description),
          url: this.urlBuilder.CreateStaticLink(
            this.urlBuilder.CreateCareServiceRouterLink(clinic.id, clinic.urlPath, service.id, service.urlPath),
          ),
          offers: offers,
        };
        knowsAbout.push(serviceSchema);
      });
      jsonSchema['knowsAbout'] = knowsAbout;
    }

    const schemaScript = this.doc.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = SchemaCodeSnippetIdEnum.MEDICAL_CLINIC;
    schemaScript.innerHTML = JSON.stringify(jsonSchema);
    return schemaScript;
  }

  private createMedicalWebPageJsonSchema(clinic: CareproviderUnitItem): any {
    const publisher = {
      '@type': 'MedicalClinic',
      name: clinic.name,
    };
    if (clinic.caregiverUnitImages) {
      const {images, thumbnailUrl, logoUrl} = clinic.caregiverUnitImages;
      if (images?.length) {
        publisher['image'] = this.tools.getImageUrl(images[0].url);
      } else if (thumbnailUrl) {
        publisher['image'] = this.tools.getImageUrl(thumbnailUrl);
      }
      if (logoUrl) {
        publisher['logo'] = this.tools.getImageUrl(logoUrl);
      }
    }

    const about = {
      '@type': 'MedicalClinic',
      name: clinic.name,
    };
    if (clinic.mainCaregiverUnitType) {
      about['medicalSpecialty'] = clinic.mainCaregiverUnitType.name;
    }

    const organization = {
      '@type': 'Organization',
      name: 'Vården Online Sverige AB',
      logo: `${this.appConfigService.appConfig.amazonS3Url}assets/varden-logo-nocross.webp`,
      url: 'https://www.varden.se',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Content team',
        email: 'info@varden.se',
        telephone: '+46-8-509-106-99',
        areaServed: 'SE',
      },
    };

    const jsonSchema = {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      about: about,
      publisher: publisher,
      headline: 'Om ' + clinic.name,
      description: this.tools.removeHtmlTags(clinic.description),
      datePublished: dayjs(clinic.updatedAt).format('YYYY-MM-DD'),
      dateModified: dayjs(clinic.updatedAt).format('YYYY-MM-DD'),
      lastReviewed: dayjs(clinic.updatedAt).format('YYYY-MM-DD'),
      reviewedBy: organization,
    };

    if (clinic.mainCaregiverUnitType) {
      jsonSchema['specialty'] = {
        '@type': 'MedicalSpecialty',
        name: clinic.mainCaregiverUnitType.name,
      };
    }

    const author = clinic.staffMembers?.map((employee) => {
      return {
        '@type': 'Person',
        name: employee.name,
        jobTitle: this.tools.removeHtmlTags(employee.description),
        memberOf: {
          '@type': 'MedicalClinic',
          name: clinic.name,
        },
      };
    });
    if (author && author.length > 0) {
      jsonSchema['author'] = author;
    }

    const schemaScript = this.doc.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = SchemaCodeSnippetIdEnum.MEDICAL_WEBPAGE;
    schemaScript.innerHTML = JSON.stringify(jsonSchema);
    return schemaScript;
  }
}
